import {
  HttpInterceptorFn,
  HttpEvent,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { switchMap, take, catchError, finalize } from 'rxjs/operators';
import { EMPTY, throwError, Observable, Subject } from 'rxjs';
import { CommonService } from '../../shared/services/common.service';
import { SecurityService } from '../services/security.service';
import { AppService } from '../services/app.service';

let isAuthErrorHandled = false;
let tokenRefreshInProgress = false;
const tokenRefreshComplete$ = new Subject<void>();

const handleWaitingRequest = (): Observable<HttpEvent<unknown>> => {
  return new Observable<HttpEvent<unknown>>(subscriber => {
    const subscription = tokenRefreshComplete$.subscribe({
      next: () => {
        subscriber.complete();
      },
      error: () => {
        subscriber.complete();
      },
    });
    return () => subscription.unsubscribe();
  });
};

const handleTokenRefresh = (
  authService: AuthService,
  appService: AppService
): Observable<HttpEvent<unknown>> => {
  tokenRefreshInProgress = true;

  return authService.getRefreshToken().pipe(
    take(1),
    switchMap(() => {
      return EMPTY;
    }),
    catchError(() => {
      if (!isAuthErrorHandled) {
        isAuthErrorHandled = true;
        localStorage.removeItem('user_data');
        localStorage.removeItem('user_role');
        localStorage.removeItem('Selected_Org_Data');
        localStorage.removeItem('auth_token');

        appService.handleGlobalAuthError();

        setTimeout(() => {
          isAuthErrorHandled = false;
        }, 3000);
      }

      return EMPTY;
    }),
    finalize(() => {
      tokenRefreshInProgress = false;
      tokenRefreshComplete$.next();
    })
  );
};

const handle401Error = (
  error: HttpEvent<unknown>,
  authService: AuthService,
  appService: AppService,
  isLoginEndpoint: boolean,
  isCurrentUserEndpoint: boolean,
  isRefreshTokenEndpoint: boolean,
  isSignOutEndpoint: boolean
): Observable<HttpEvent<unknown>> => {
  if (isSignOutEndpoint) {
    authService.clearAuthDataAndRedirect();
    return EMPTY;
  }

  if (isLoginEndpoint || isCurrentUserEndpoint || isRefreshTokenEndpoint) {
    return throwError(() => error);
  }

  if (
    localStorage.getItem('verify-user') ||
    localStorage.getItem('isOtpVerified')
  ) {
    return throwError(() => error);
  }

  if (tokenRefreshInProgress) {
    return handleWaitingRequest();
  }

  return handleTokenRefresh(authService, appService);
};

const validateAndCleanRequestBody = (
  req: HttpRequest<unknown>,
  securityService: SecurityService,
  isSamlConfigEndpoint: boolean,
  isSignOutEndpoint: boolean
): HttpRequest<unknown> => {
  if (!(req.body && typeof req.body === 'object' && !isSamlConfigEndpoint)) {
    return req;
  }

  let isSecurityIssueDetected = false;
  const cleanedBody: Record<string, unknown> = { ...req.body };
  const skipValidationFields = isSignOutEndpoint
    ? ['sloUrl', 'idpName']
    : ['body', 'profileImage', 'message'];

  for (const [key, value] of Object.entries(req.body)) {
    if (typeof value === 'string' && !skipValidationFields.includes(key)) {
      const validationResult = securityService.validateInput(value);
      if (!validationResult.isValid) {
        isSecurityIssueDetected = true;
        securityService.showSecurityAlert(
          validationResult.issue || 'Suspicious content',
          key
        );
        delete cleanedBody[key];
      }
    }
  }

  return isSecurityIssueDetected ? req.clone({ body: cleanedBody }) : req;
};

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const common = inject(CommonService);
  const securityService = inject(SecurityService);
  const appService = inject(AppService);
  const isLoginEndpoint = req.url.includes('sign-in');
  const isCurrentUserEndpoint = req.url.includes('get-current-user');
  const isRefreshTokenEndpoint = req.url.includes('validate-refresh-token');
  const isMoodleEndpoint =
    req.url.includes('dev-moodle.insightstobehavior.net') ||
    req.url.includes('stage-moodle.insightstobehavior.net');
  const isSamlConfigEndpoint = req.url.includes('samlconfiguration');
  const isSignOutEndpoint = req.url.includes('sign-out');

  let modifiedReq = validateAndCleanRequestBody(
    req,
    securityService,
    isSamlConfigEndpoint,
    isSignOutEndpoint
  );

  return authService.authState$.pipe(
    take(1),
    switchMap(() => {
      modifiedReq = modifiedReq.clone({
        setHeaders: {
          Accept: 'application/json',
          ...(isMoodleEndpoint ? {} : securityService.getSecurityHeaders()),
        },
        withCredentials: true,
      });

      return next(modifiedReq).pipe(
        catchError(error => {
          console.error('HTTP Error in AuthInterceptor:', error);
          if (error.status === 401) {
            return handle401Error(
              error,
              authService,
              appService,
              isLoginEndpoint,
              isCurrentUserEndpoint,
              isRefreshTokenEndpoint,
              isSignOutEndpoint
            );
          }

          if (error.status === 403) {
            common.notify(
              'error',
              'Access denied. Please check your permissions.'
            );
            return EMPTY;
          }

          if (error.status === 429) {
            common.notify(
              'error',
              'A process is currently ongoing. Please try again in 1 minute.'
            );
            return EMPTY;
          }
          return throwError(() => error);
        })
      );
    })
  );
};
